import React, { useState, useContext, useRef, useEffect, useReducer } from 'react';
import { getInfoFromAppData } from './DetailsPage.helpers';
import { Link } from 'react-router-dom';
import VisibilitySensor from 'react-visibility-sensor';
import Scrollbar from "react-custom-scrollbars";
import Wrapper from "../../components/Wrapper/Wrapper";
import CircularProgressBar from "../../components/CircularProgressBar/CircularProgressBar";
import ScrollIcon from "../../icons/scroll-icon.svg";
import './DetailsPage.styles.scss';
import { AppContext, CARDS_PAGE_PATH } from '../../App';
import { isEmpty } from "../../core/helpers";
import PageHeader from "../../components/PageHeader/PageHeader";
import PageFooter from "../../components/PageFooter/PageFooter";
import DemographicFilters from "../../components/DemographicFilters/DemographicFilters";
import http, { RESPONSE_TYPES } from "../../core/http";
import { normalizeData } from "./DetailsPage.helpers";
import '../../components/ComplexChart/ComplexChart.styles.scss';
import { PERMISSION_LEVEL_ENUM } from "../../App.helpers";
import Modal from "../../components/Modal/Modal";
import Loader from "../../components/Loader/Loader";
import SectionWhere from "./SectionWhere";
import SectionWhen from "./SectionWhen";
import SectionWhy from "./SectionWhy";
import SectionDecision from "./SectionDecision";
import SectionDisruption from "./SectionDisruption";
import SectionWhat from "./SectionWhat";

const replaceData = (oldData, newData) => newData;

function DetailsPage({ match, history }) {
    const {
        data: appData,
        filters,
        credentials,
    } = useContext(AppContext);
    const id = +match.params.id;

    const scrollbarElement = useRef(null);
    const image = useRef(null);
    const footer = useRef(null);
    const footerLeft = useRef(null);
    const footerRight = useRef(null);
    const contentRef = useRef(null);
    const whereRef = useRef(null);
    const whenRef = useRef(null);
    const whyRef = useRef(null);
    const sequenceRef = useRef(null);
    const disruptionRef = useRef(null);
    const whatRef = useRef(null);
    const microDriversRef = useRef(null);

    const [activeMicroDriverCategoryName, setActiveMicroDriverCategoryName] = useState('');
    const [isDemographicFiltersOpened, setIsDemographicFiltersOpened] = useState(false);
    const [scrollMenuShown, setScrollMenuShown] = useState(false);

    const [isLoadingPassed, setIsLoadingPassed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [isWhereSensorActive, setIsWhereSensorActive] = useState(false);
    const [isWhenSensorActive, setIsWhenSensorActive] = useState(false);
    const [isWhySensorActive, setIsWhySensorActive] = useState(false);
    const [isSequenceSensorActive, setIsSequenceSensorActive] = useState(false);
    const [isDisruptionSensorActive, setIsDisruptionSensorActive] = useState(false);
    const [isWhatSensorActive, setIsWhatSensorActive] = useState(false);
    const [data, setData] = useReducer(replaceData, {});
    const [activeFilterIds, setActiveFilterIds] = useState([]);

    const manageLinkAccordingToPermissions = ({ link, ...item }) => {
        if (credentials.permissionLevel < PERMISSION_LEVEL_ENUM.SUBSCRIPTION) {
            return item;
        } else {
            return {
                ...item,
                link,
            };
        }
    };

    const {
        headerData = {},
        decisionPointData,
        consumptionPointData,
        foodSourceData = [
            {},
            {
                events: [
                    {},
                    {},
                    { events: [], }
                ]
            }
        ],
        segmentShareData = [[], []],
        obtainedData = [],
        occasionsData = [],
        triggersData = [],
        megaDriversData = [],
        topSpecificNeeds = [],
        highlyIndexedNeeds = [],
        microDriversData = [],
        timeTotalChartData = [],
        timeChartData = [],
        sequence = [],
        foodTypeConsumedData = [],
        cravingsData = {},
        atHomeEventsData = {},
        awayFromHomeEventsData = {},
    } = data;

    const {
        name,
        categoryName,
        categoryLink,
        renderCategoryTitle,
    } = getInfoFromAppData(appData, id, history);

    useEffect(() => {
        setActiveFilterIds([]);
    }, [match.params.id]);

    const getLink = (api) => {
        let url = `${api}?id=${id}`;
        activeFilterIds.forEach((activeFilterId) => {
            url += `&filters=${activeFilterId}`;
        });
        return url;
    };


    useEffect(() => {
        setData({});
        setIsDemographicFiltersOpened(false);
        if (isLoadingPassed) {
            setIsLoading(true);
        } else {
            setIsLoadingPassed(true);
        }
        setActiveMicroDriverCategoryName('');
        const startTime = (new Date()).getTime();


        http.get(getLink('data/get')).then((responseData) => {
            setData(normalizeData(responseData));
            const endTime = (new Date()).getTime();
            if (endTime - startTime > 2000) {
                setIsLoading(false);
            } else {
                setTimeout(() => {
                    setIsLoading(false);
                }, endTime - startTime);
            }
        });
    }, [match.params.id, activeFilterIds]);

    useEffect(() => {
        if (activeMicroDriverCategoryName !== '' && microDriversRef.current) {
            microDriversRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [activeMicroDriverCategoryName]);

    const scrollToContent = () => {
        if (contentRef.current) {
            contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const scrollToTop = () => {
        if (image.current) {
            image.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const scrollbarControl = ({ target }) => {
        const scrollTop = target.scrollTop;
        const coefficient = Math.min(1, scrollTop / (window.innerHeight - 64));

        image.current.style.backgroundPositionY = `${scrollTop / 2}px`;
        setScrollMenuShown(coefficient >= .95);
        footer.current.style.height = `${160 - 80 * coefficient}px`;
        footer.current.style.marginTop = `-${160 - 80 * coefficient}px`;
        footerLeft.current.style.transform = `scale(${1 - coefficient / 2})`;
        footerRight.current.style.transform = `scale(${1 - coefficient / 2})`;
        coefficient >= 0.98 ? footer.current.classList.add('isFixedToTop') : footer.current.classList.remove('isFixedToTop');
    };

    useEffect(() => {
        if (scrollbarElement.current) {
            scrollbarControl({ target: scrollbarElement.current });
        }
    }, [scrollbarElement, isLoading]);

    const microDriversActiveCategory = microDriversData.find(({ name }) => name === activeMicroDriverCategoryName);

    const downloadFile = () => {
        setIsLoading(true);
        http.post(getLink('export/post'), RESPONSE_TYPES.TEXT).then((downloadLink) => {
            const a = document.createElement('A');
            a.href = downloadLink;
            a.click();
            setIsLoading(false);
        });
    };

    return (
        <>
            <Loader isActive={isLoading}/>
            <PageHeader isDetailsPage={true}/>
            <div className="details-page">
                {!isLoading && (
                    <Scrollbar
                        onScroll={scrollbarControl}
                        ref={scrollbarElement}
                        renderThumbVertical={() => (
                            <div className="scrollbar__thumb"/>
                        )}
                    >
                        <div
                            className="details-page__top"
                            ref={image}
                            style={{
                                backgroundImage: `url(${process.env.REACT_APP_BASE_ROUTE}thumb-single/${id}.jpeg)`,
                            }}
                        >
                            <div className="details-page__top-btn">
                                <Link
                                    to={`${CARDS_PAGE_PATH}/${categoryLink}`}
                                    className="btn btn--primary"
                                >
                                    back
                                </Link>
                            </div>
                        </div>
                        {!isEmpty(data) && (
                            <div className="details-page__scroll-btn" onClick={() => scrollToContent()}>
                                <ScrollIcon/>
                            </div>
                        )}
                        <div className="details-page__top-footer" ref={footer}>
                            <Wrapper>
                                <div className="top-widget">
                                    <div className="top-widget__left" ref={footerLeft}>
                                        <div className="top-widget__title">{name}</div>
                                        <div className="top-widget__subtitle">{categoryName}</div>
                                    </div>
                                    <div className="top-widget__center">
                                        <nav
                                            className={`details-page-nav tabs details-page-nav--${scrollMenuShown ? 'visible' : 'hidden'}`}
                                        >
                                            <div
                                                className={`tabs__title ${isWhereSensorActive && !isWhenSensorActive ? 'tabs__title--active' : ''}`}
                                                onClick={() => whereRef.current.scrollIntoView({
                                                    behavior: 'smooth',
                                                    block: 'start'
                                                })}
                                            >
                                                Where
                                            </div>
                                            <div
                                                className={`tabs__title ${isWhenSensorActive && !isWhySensorActive ? 'tabs__title--active' : ''}`}
                                                onClick={() => whenRef.current.scrollIntoView({
                                                    behavior: 'smooth',
                                                    block: 'start'
                                                })}
                                            >
                                                When
                                            </div>
                                            <div
                                                className={`tabs__title ${isWhySensorActive && !isSequenceSensorActive ? 'tabs__title--active' : ''}`}
                                                onClick={() => whyRef.current.scrollIntoView({
                                                    behavior: 'smooth',
                                                    block: 'start'
                                                })}
                                            >
                                                Why
                                            </div>
                                            <div
                                                className={`tabs__title ${isSequenceSensorActive && !isDisruptionSensorActive ? 'tabs__title--active' : ''}`}
                                                onClick={() => sequenceRef.current.scrollIntoView({
                                                    behavior: 'smooth',
                                                    block: 'start'
                                                })}
                                            >
                                                Decision
                                            </div>
                                            <div
                                                className={`tabs__title ${isDisruptionSensorActive && !isWhatSensorActive ? 'tabs__title--active' : ''}`}
                                                onClick={() => disruptionRef.current.scrollIntoView({
                                                    behavior: 'smooth',
                                                    block: 'start'
                                                })}
                                            >
                                                Disruption
                                            </div>
                                            <div
                                                className={`tabs__title ${isWhatSensorActive ? 'tabs__title--active' : ''}`}
                                                onClick={() => whatRef.current.scrollIntoView({
                                                    behavior: 'smooth',
                                                    block: 'start'
                                                })}
                                            >
                                                What
                                            </div>
                                        </nav>
                                    </div>
                                    <div className="top-widget__right" ref={footerRight}>
                                        {credentials.permissionLevel > PERMISSION_LEVEL_ENUM.REGULAR && (
                                            <button
                                                className="top-widget__download-btn"
                                                onClick={downloadFile}
                                            >
                                                Download PPT
                                            </button>
                                        )}
                                        <div className="top-widget__events">
                                            <span>{headerData.eatingEvents || 0}%</span>
                                            of eating<br/>events
                                        </div>
                                        <div className="top-widget__chart">
                                            <div className="pie-chart">
                                                <div className="pie-chart__chart">
                                                    <CircularProgressBar
                                                        value={headerData.awayHome || 0}
                                                        size={96}
                                                    />
                                                </div>
                                                <div className="pie-chart__title">AFH</div>
                                            </div>
                                        </div>
                                        <div className="top-widget__chart">
                                            <div className="pie-chart">
                                                <div className="pie-chart__chart">
                                                    <CircularProgressBar
                                                        value={headerData.plannedSpontaneous || 0}
                                                        size={96}
                                                    />
                                                </div>
                                                <div className="pie-chart__title">
                                                    {headerData.plannedSpontaneousName}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Wrapper>
                        </div>
                        {!isEmpty(data) && (
                            <div className="details-page__content">
                                <div ref={contentRef} style={{ transform: 'translateY(-120px)' }}/>

                                <Wrapper>
                                    <div className="details-charts">
                                        <div className="details-charts__top">

                                            <div className="details-charts__title">
                                                {renderCategoryTitle(headerData.eatingEvents)}
                                            </div>
                                            <div className="details-charts__subtitle">
                                                HERE’S WHAT THOSE EVENTS LOOK LIKE.
                                            </div>
                                            <div className="details-charts__text">
                                                (but remember, what consumers want isn’t always the same as what they
                                                choose)
                                            </div>
                                        </div>
                                        <div ref={whereRef} style={{ transform: 'translateY(-60px)' }}/>
                                        <VisibilitySensor
                                            key="1"
                                            partialVisibility={true}
                                            offset={{ top: 10 }}
                                            onChange={isVisible => setIsWhereSensorActive(isVisible)}
                                        >
                                            <div className="details-charts__content">
                                                <SectionWhere
                                                    foodSourceData={foodSourceData}
                                                    decisionPointData={decisionPointData}
                                                    consumptionPointData={consumptionPointData}
                                                    segmentShareData={segmentShareData}
                                                    manageLinkAccordingToPermissions={manageLinkAccordingToPermissions}
                                                    obtainedData={obtainedData}
                                                />
                                            </div>
                                        </VisibilitySensor>
                                        <div ref={whenRef} style={{ transform: 'translateY(-60px)' }}/>
                                        <VisibilitySensor
                                            key="2"
                                            partialVisibility={true}
                                            offset={{ bottom: 400 }}
                                            onChange={isVisible => setIsWhenSensorActive(isVisible)}
                                        >
                                            <div className="details-charts__content">
                                                <SectionWhen
                                                    timeTotalChartData={timeTotalChartData}
                                                    timeChartData={timeChartData}
                                                    occasionsData={occasionsData}
                                                />
                                            </div>
                                        </VisibilitySensor>
                                        <div ref={whyRef} style={{ transform: 'translateY(-60px)' }}/>
                                        <VisibilitySensor
                                            key="3"
                                            partialVisibility={true}
                                            offset={{ bottom: 400 }}
                                            onChange={isVisible => setIsWhySensorActive(isVisible)}
                                        >
                                            <div className="details-charts__content">
                                                <SectionWhy
                                                    triggersData={triggersData}
                                                    megaDriversData={megaDriversData}
                                                    manageLinkAccordingToPermissions={manageLinkAccordingToPermissions}
                                                    highlyIndexedNeeds={highlyIndexedNeeds}
                                                    topSpecificNeeds={topSpecificNeeds}
                                                    forwardRef={microDriversRef}
                                                    microDriversData={microDriversData}
                                                    activeMicroDriverCategoryName={activeMicroDriverCategoryName}
                                                    setActiveMicroDriverCategoryName={setActiveMicroDriverCategoryName}
                                                    microDriversActiveCategory={microDriversActiveCategory}
                                                    name={name}
                                                />
                                            </div>
                                        </VisibilitySensor>
                                        <div ref={sequenceRef} style={{ transform: 'translateY(-60px)' }}/>
                                        <VisibilitySensor
                                            key="4"
                                            partialVisibility={true}
                                            offset={{ bottom: 400 }}
                                            onChange={isVisible => setIsSequenceSensorActive(isVisible)}
                                        >
                                            <div className="details-charts__content">
                                                <SectionDecision
                                                    sequence={sequence}
                                                />
                                            </div>
                                        </VisibilitySensor>
                                        <div ref={disruptionRef} style={{ transform: 'translateY(-60px)' }}/>
                                        <VisibilitySensor
                                            key="5"
                                            partialVisibility={true}
                                            offset={{ bottom: 400 }}
                                            onChange={isVisible => setIsDisruptionSensorActive(isVisible)}
                                        >
                                            <div className="details-charts__content">
                                                <SectionDisruption
                                                    cravingsData={cravingsData}
                                                />
                                            </div>
                                        </VisibilitySensor>
                                        <div ref={whatRef} style={{ transform: 'translateY(-60px)' }}/>
                                        <VisibilitySensor
                                            key="6"
                                            partialVisibility={true}
                                            offset={{ bottom: 400 }}
                                            onChange={isVisible => setIsWhatSensorActive(isVisible)}
                                        >
                                            <div
                                                className="details-charts__content"
                                            >
                                                <SectionWhat
                                                    foodTypeConsumedData={foodTypeConsumedData}
                                                    atHomeEventsData={atHomeEventsData}
                                                    awayFromHomeEventsData={awayFromHomeEventsData}
                                                />
                                            </div>
                                        </VisibilitySensor>
                                    </div>
                                </Wrapper>

                                <div className="details-page__scroll-to-top-btn" onClick={() => scrollToTop()}>
                                    <ScrollIcon/>
                                </div>

                            </div>
                        )}
                    </Scrollbar>
                )}

                {credentials.permissionLevel > PERMISSION_LEVEL_ENUM.REGULAR && (
                    <div className="filters-btn">
                        <div
                            className={`filter-button btn btn--filters ${activeFilterIds.length > 0 ? 'btn--active' : ''}`}
                            onClick={() => setIsDemographicFiltersOpened(true)}
                        >
                            demographic filters
                            <span className="filter-button--after"/>
                        </div>
                    </div>
                )}

                <Modal
                    isOpened={isDemographicFiltersOpened}
                    close={() => setIsDemographicFiltersOpened(false)}
                    closeOnOverlayClick={false}
                >
                    <DemographicFilters
                        isOpened={isDemographicFiltersOpened}
                        data={filters}
                        setActiveFilterIds={setActiveFilterIds}
                        activeFilterIds={activeFilterIds}
                        close={() => setIsDemographicFiltersOpened(false)}
                    />
                </Modal>

                <PageFooter/>
            </div>
        </>
    );
}

export default DetailsPage;